import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode, type TouchEvent as ReactTouchEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addDays,
  differenceInCalendarDays,
  differenceInMinutes,
  endOfWeek,
  format,
  getDay,
  isSameWeek,
  isToday,
  parseISO,
  startOfDay,
  startOfWeek,
} from 'date-fns'
import { de } from 'date-fns/locale'
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2Icon,
  MapPin,
  Users,
} from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { SessionDetailDialog, type SessionDetailData } from '@/components/session-detail-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { getCurrentUserRole, getMemberBookings, getPublicSessions, getUserSubscriptionStatus, bookSessionWithCredit, cancelMemberBooking } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { scrollAppLayoutToTop } from '@/lib/scroll-container'
import { isRecurringSubscription } from '@/lib/subscriptions'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import type { Booking, PublicSession } from '@/types/domain'

import './member-calendar-page.css'

const DAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']
const MOBILE_FETCH_PADDING_DAYS = 10
const MOBILE_SWIPE_MIN_THRESHOLD_PX = 52
const MOBILE_SWIPE_MAX_THRESHOLD_PX = 92
const MOBILE_SWIPE_VELOCITY_THRESHOLD = 0.28
const MOBILE_SWIPE_PROJECTED_TRAVEL_MS = 140
const MOBILE_SWIPE_MAX_DRAG_RATIO = 0.94
const MOBILE_SWIPE_RESISTANCE_START_RATIO = 0.24
const MOBILE_SWIPE_MIN_SETTLE_DURATION_MS = 160
const MOBILE_SWIPE_MAX_SETTLE_DURATION_MS = 280

type CalendarCourse = {
  id: string
  session: PublicSession
  name: string
  time: string
  duration: number
  day: number
  dateKey: string
  spots: number
  resourceName: string | null
  state: 'available' | 'booked' | 'full' | 'past'
}

function bookingToSessionDetail(booking: Booking): SessionDetailData {
  const courseSession = booking.course_sessions
  const availableSpots = courseSession
    ? courseSession.max_participants - courseSession.confirmed_count
    : null

  return {
    courseName: courseSession?.courses?.name ?? 'Kurs',
    courseDescription: courseSession?.courses?.description ?? null,
    startsAt: courseSession?.starts_at ?? '',
    endsAt: courseSession?.ends_at ?? '',
    trainerName: courseSession?.trainer_name ?? null,
    trainerId: courseSession?.trainer_id ?? null,
    level: courseSession?.courses?.level ?? null,
    availableSpots,
    maxParticipants: courseSession?.max_participants ?? null,
    status: booking.status === 'confirmed' ? 'confirmed' : 'cancelled',
    registrationUntil: courseSession?.registration_until ?? null,
    cancellationUntil: courseSession?.cancellation_until ?? null,
    minParticipants: courseSession?.min_participants ?? null,
    confirmedCount: courseSession?.confirmed_count ?? null,
    resourceName: courseSession?.resource_name ?? null,
  }
}

function publicSessionToDetail(session: PublicSession): SessionDetailData {
  return {
    courseName: session.course_name,
    courseDescription: session.description,
    startsAt: session.starts_at,
    endsAt: session.ends_at,
    trainerName: session.trainer_name,
    trainerId: session.trainer_id,
    level: session.level,
    availableSpots: session.available_spots,
    maxParticipants: session.max_participants,
    registrationUntil: session.registration_until,
    cancellationUntil: session.cancellation_until,
    minParticipants: session.min_participants,
    confirmedCount: session.confirmed_count,
    resourceName: session.resource_name,
  }
}

function getCourseState(session: PublicSession, isBooked: boolean): CalendarCourse['state'] {
  if (isBooked) {
    return 'booked'
  }

  if (new Date(session.starts_at) < new Date()) {
    return 'past'
  }

  if (session.available_spots <= 0) {
    return 'full'
  }

  return 'available'
}

function getActionLabel(state: CalendarCourse['state']) {
  if (state === 'booked') {
    return 'Gebucht'
  }

  if (state === 'past') {
    return 'Vergangen'
  }

  if (state === 'full') {
    return 'Ausgebucht'
  }

  return 'Buchen'
}

function getActionClassName(state: CalendarCourse['state']) {
  if (state === 'booked') {
    return 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20'
  }

  if (state === 'past' || state === 'full') {
    return 'border-border/40 bg-muted/30 text-muted-foreground hover:bg-muted/40'
  }

  return 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/20'
}

function ScrollFadeContainer({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [showBottomFade, setShowBottomFade] = useState(false)

  function updateBottomFadeVisibility() {
    const container = scrollRef.current
    if (!container) {
      return
    }

    const hasOverflow = container.scrollHeight - container.clientHeight > 8
    const distanceToBottom = container.scrollHeight - container.clientHeight - container.scrollTop
    setShowBottomFade(hasOverflow && distanceToBottom > 12)
  }

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      updateBottomFadeVisibility()
    })

    return () => {
      window.cancelAnimationFrame(frame)
    }
  }, [children])

  useEffect(() => {
    const container = scrollRef.current
    if (!container || typeof ResizeObserver === 'undefined') {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      updateBottomFadeVisibility()
    })

    resizeObserver.observe(container)

    const firstChild = container.firstElementChild
    if (firstChild instanceof HTMLElement) {
      resizeObserver.observe(firstChild)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [children])

  return (
    <div className="relative min-h-0">
      <div
        ref={scrollRef}
        onScroll={updateBottomFadeVisibility}
        className={cn(
          'motionlab-member-calendar-day-scroll',
          showBottomFade && 'motionlab-member-calendar-day-scroll--bottom-fade',
          className,
        )}
      >
        {children}
      </div>
    </div>
  )
}

export function MemberCalendarPage() {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const isMobile = useIsMobile()
  const desktopWeekScrollContainerRef = useRef<HTMLDivElement | null>(null)
  const mobileSwipeViewportRef = useRef<HTMLDivElement | null>(null)
  const mobileSwipeTrackRef = useRef<HTMLDivElement | null>(null)
  const mobileSwipeOffsetRef = useRef(0)
  const mobileCourseAreaSwipeRef = useRef({
    active: false,
    axis: null as 'x' | 'y' | null,
    startX: 0,
    startY: 0,
    lastX: 0,
    startTime: 0,
    lastTime: 0,
    triggered: false,
  })
  const mobileSwipeMotionRef = useRef<'idle' | 'snapback' | 'settling-prev' | 'settling-next'>('idle')
  const mobileSuppressCourseAreaClickRef = useRef(false)
  const earliestVisibleDate = useMemo(() => startOfWeek(startOfDay(new Date()), { weekStartsOn: 1 }), [])
  const initialDate = useMemo(() => {
    const dateParam = searchParams.get('date')
    if (!dateParam) {
      return startOfDay(new Date())
    }

    const parsedDate = startOfDay(parseISO(dateParam))
    if (Number.isNaN(parsedDate.getTime())) {
      return startOfDay(new Date())
    }

    return parsedDate < earliestVisibleDate ? earliestVisibleDate : parsedDate
  }, [earliestVisibleDate, searchParams])
  const mobileSelectedDateRef = useRef(initialDate)

  const [currentDate, setCurrentDate] = useState(initialDate)
  const [mobileSelectedDate, setMobileSelectedDate] = useState(initialDate)
  const [mobileRangeStartDate, setMobileRangeStartDate] = useState(() =>
    addDays(startOfDay(initialDate), -MOBILE_FETCH_PADDING_DAYS) < earliestVisibleDate
      ? earliestVisibleDate
      : addDays(startOfDay(initialDate), -MOBILE_FETCH_PADDING_DAYS),
  )
  const [mobileRangeEndDate, setMobileRangeEndDate] = useState(() =>
    addDays(startOfDay(initialDate), MOBILE_FETCH_PADDING_DAYS),
  )
  const [bookingSession, setBookingSession] = useState<PublicSession | null>(null)
  const [cancellingBooking, setCancellingBooking] = useState<Booking | null>(null)

  useLayoutEffect(() => {
    scrollAppLayoutToTop()
  }, [])

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 })
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 })
  const isAtEarliestVisibleWeek = isSameWeek(currentDate, earliestVisibleDate, { weekStartsOn: 1 })
  const isAtEarliestVisibleMobileDate = mobileSelectedDate <= earliestVisibleDate
  const mobileRangeStart = mobileRangeStartDate
  const mobileRangeEnd = mobileRangeEndDate
  const calendarStart = isMobile ? mobileRangeStart : weekStart
  const calendarEnd = isMobile ? mobileRangeEnd : weekEnd

  const sessionsQuery = useQuery({
    queryKey: queryKeys.publicSessions(calendarStart.toISOString(), addDays(calendarEnd, 1).toISOString()),
    queryFn: () => getPublicSessions(calendarStart.toISOString(), addDays(calendarEnd, 1).toISOString()),
  })

  const bookingsQuery = useQuery({
    queryKey: queryKeys.memberBookings,
    queryFn: () => getMemberBookings({}),
    retry: false,
    refetchOnWindowFocus: false,
  })

  const profileQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const subscriptionQuery = useQuery({
    queryKey: queryKeys.userSubscription,
    queryFn: getUserSubscriptionStatus,
    enabled: profileQuery.data?.role === 'member',
  })

  const bookMutation = useMutation({
    mutationFn: bookSessionWithCredit,
    onSuccess: () => {
      toast.success('Kurs erfolgreich gebucht!')
      setBookingSession(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.publicSessionsBase })
      queryClient.invalidateQueries({ queryKey: queryKeys.userSubscription })
      queryClient.invalidateQueries({ queryKey: queryKeys.memberBookings })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const cancelMutation = useMutation({
    mutationFn: cancelMemberBooking,
    onSuccess: () => {
      toast.success('Buchung erfolgreich storniert.')
      setCancellingBooking(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.publicSessionsBase })
      queryClient.invalidateQueries({ queryKey: queryKeys.userSubscription })
      queryClient.invalidateQueries({ queryKey: queryKeys.memberBookings })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const bookingsBySessionId = useMemo(() => {
    const map: Record<string, Booking> = {}

    for (const booking of bookingsQuery.data ?? []) {
      if (booking.status === 'confirmed' && booking.session_id) {
        map[booking.session_id] = booking
      }
    }

    return map
  }, [bookingsQuery.data])

  const mappedCourses = useMemo<CalendarCourse[]>(() => {
    return (sessionsQuery.data ?? []).map((session) => {
      const startDate = parseISO(session.starts_at)
      const endDate = parseISO(session.ends_at)
      const state = getCourseState(session, Boolean(bookingsBySessionId[session.session_id]))
      const parsedSpots = Number(session.available_spots)

      return {
        id: session.session_id,
        session,
        name: session.course_name,
        time: format(startDate, 'HH:mm'),
        duration: differenceInMinutes(endDate, startDate),
        day: (getDay(startDate) + 6) % 7,
        dateKey: format(startDate, 'yyyy-MM-dd'),
        spots: Number.isFinite(parsedSpots) ? Math.max(parsedSpots, 0) : 0,
        resourceName: session.resource_name ?? null,
        state,
      }
    })
  }, [bookingsBySessionId, sessionsQuery.data])

  const coursesByDate = useMemo(() => {
    return mappedCourses.reduce<Record<string, CalendarCourse[]>>((grouped, course) => {
      if (!grouped[course.dateKey]) {
        grouped[course.dateKey] = []
      }

      grouped[course.dateKey].push(course)
      return grouped
    }, {})
  }, [mappedCourses])

  const mobileVisibleDays = useMemo(
    () => [
      addDays(mobileSelectedDate, -1) < earliestVisibleDate
        ? earliestVisibleDate
        : addDays(mobileSelectedDate, -1),
      mobileSelectedDate,
      addDays(mobileSelectedDate, 1),
    ],
    [earliestVisibleDate, mobileSelectedDate],
  )

  const initialHorizontalDayIndex = (getDay(currentDate) + 6) % 7
  const desktopDayColumnClassName =
    'w-full max-w-full min-[700px]:min-w-[calc((100%-24px)/3)] min-[700px]:max-w-[calc((100%-24px)/3)] min-[1030px]:min-w-0 min-[1030px]:max-w-none flex-shrink-0 snap-start'

  function clampMobileSwipeValue(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max)
  }

  function getMobileSwipeThreshold(width: number) {
    return clampMobileSwipeValue(
      width * 0.18,
      MOBILE_SWIPE_MIN_THRESHOLD_PX,
      MOBILE_SWIPE_MAX_THRESHOLD_PX,
    )
  }

  function getMobileSwipeOffsetWithResistance(offsetPx: number, width: number) {
    const maxOffset = width > 0 ? width * MOBILE_SWIPE_MAX_DRAG_RATIO : 280
    const resistanceStart = width > 0 ? width * MOBILE_SWIPE_RESISTANCE_START_RATIO : 72
    const direction = Math.sign(offsetPx) || 1
    const absoluteOffset = Math.abs(offsetPx)

    if (absoluteOffset <= resistanceStart) {
      return clampMobileSwipeValue(offsetPx, -maxOffset, maxOffset)
    }

    const extraOffset = absoluteOffset - resistanceStart
    const resistanceDenominator = Math.max(width * 0.55, 180)
    const resistedExtraOffset = extraOffset / (1 + extraOffset / resistanceDenominator)

    return clampMobileSwipeValue(
      direction * (resistanceStart + resistedExtraOffset),
      -maxOffset,
      maxOffset,
    )
  }

  function getMobileSwipeSettleDuration(targetOffsetPx: number, width: number) {
    if (width <= 0) {
      return MOBILE_SWIPE_MAX_SETTLE_DURATION_MS
    }

    const distance = Math.abs(targetOffsetPx - mobileSwipeOffsetRef.current)
    const normalizedDistance = clampMobileSwipeValue(distance / width, 0, 1)

    return Math.round(
      MOBILE_SWIPE_MIN_SETTLE_DURATION_MS +
      (MOBILE_SWIPE_MAX_SETTLE_DURATION_MS - MOBILE_SWIPE_MIN_SETTLE_DURATION_MS) * normalizedDistance,
    )
  }

  function setMobileSwipeTrackOffset(offsetPx: number, animate: boolean, durationMs?: number) {
    const track = mobileSwipeTrackRef.current
    if (!track) {
      return
    }

    mobileSwipeOffsetRef.current = offsetPx
    track.style.transition = animate
      ? `transform ${durationMs ?? MOBILE_SWIPE_MAX_SETTLE_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
      : 'none'
    track.style.transform = `translate3d(calc(-33.333333% + ${offsetPx}px), 0, 0)`
  }

  useLayoutEffect(() => {
    if (!isMobile) {
      return
    }

    mobileSelectedDateRef.current = mobileSelectedDate
    setMobileSwipeTrackOffset(0, false)
    mobileSwipeMotionRef.current = 'idle'
  }, [isMobile, mobileSelectedDate])

  const isMember = profileQuery.data?.role === 'member'
  const outOfCredits =
    isMember &&
    subscriptionQuery.data &&
    subscriptionQuery.data.remaining_credits <= 0 &&
    !isRecurringSubscription(subscriptionQuery.data)

  const isBookingDeadlinePassed = bookingSession?.registration_until
    ? new Date(bookingSession.registration_until) <= new Date()
    : false
  const isBookingSessionPast = bookingSession ? new Date(bookingSession.starts_at) < new Date() : false
  const isBookingSessionFull = bookingSession ? bookingSession.available_spots <= 0 : false

  function openSessionDialog(session: PublicSession) {
    const existingBooking = bookingsBySessionId[session.session_id]

    if (existingBooking) {
      setCancellingBooking(existingBooking)
      setBookingSession(null)
      return
    }

    setBookingSession(session)
    setCancellingBooking(null)
  }

  function resetMediumDesktopWeekScroll() {
    if (typeof window === 'undefined') {
      return
    }

    if (window.innerWidth < 700 || window.innerWidth >= 1030) {
      return
    }

    desktopWeekScrollContainerRef.current?.scrollTo({
      left: 0,
      behavior: 'auto',
    })
  }

  function handlePrevWeek() {
    if (isAtEarliestVisibleWeek) {
      return
    }

    resetMediumDesktopWeekScroll()
    setCurrentDate((previousDate) => {
      const previousWeek = startOfWeek(addDays(previousDate, -7), { weekStartsOn: 1 })
      return previousWeek < earliestVisibleDate ? earliestVisibleDate : previousWeek
    })
  }

  function handleNextWeek() {
    resetMediumDesktopWeekScroll()
    setCurrentDate((previousDate) => startOfWeek(addDays(previousDate, 7), { weekStartsOn: 1 }))
  }

  function extendMobileRangeIfNeeded(nextDate: Date) {
    const distanceToStart = differenceInCalendarDays(nextDate, mobileRangeStart)
    const distanceToEnd = differenceInCalendarDays(mobileRangeEnd, nextDate)

    if (distanceToEnd <= 3) {
      setMobileRangeEndDate(addDays(mobileRangeEnd, MOBILE_FETCH_PADDING_DAYS))
    }

    if (distanceToStart <= 3) {
      const nextRangeStart = addDays(mobileRangeStart, -MOBILE_FETCH_PADDING_DAYS)
      setMobileRangeStartDate(nextRangeStart < earliestVisibleDate ? earliestVisibleDate : nextRangeStart)
    }
  }

  function handleToday() {
    const today = startOfDay(new Date())
    mobileSelectedDateRef.current = today
    setCurrentDate(today)
    setMobileSelectedDate(today)
    setMobileRangeStartDate(
      addDays(today, -MOBILE_FETCH_PADDING_DAYS) < earliestVisibleDate
        ? earliestVisibleDate
        : addDays(today, -MOBILE_FETCH_PADDING_DAYS),
    )
    setMobileRangeEndDate(addDays(today, MOBILE_FETCH_PADDING_DAYS))
    mobileSuppressCourseAreaClickRef.current = false
    mobileSwipeMotionRef.current = 'idle'
    setMobileSwipeTrackOffset(0, false)
  }

  function getMobileSwipeViewportWidth() {
    return mobileSwipeViewportRef.current?.getBoundingClientRect().width ?? 0
  }

  function commitMobileDayChange(nextDate: Date) {
    const clampedNextDate = nextDate < earliestVisibleDate ? earliestVisibleDate : nextDate
    mobileSelectedDateRef.current = clampedNextDate
    setMobileSelectedDate(clampedNextDate)
    setCurrentDate(clampedNextDate)
    extendMobileRangeIfNeeded(clampedNextDate)
  }

  function flushMobileSwipeMotion() {
    const motion = mobileSwipeMotionRef.current
    if (motion === 'idle') {
      return mobileSelectedDateRef.current
    }

    mobileSwipeMotionRef.current = 'idle'
    setMobileSwipeTrackOffset(0, false)

    if (motion === 'snapback') {
      return mobileSelectedDateRef.current
    }

    const nextDate = addDays(mobileSelectedDateRef.current, motion === 'settling-next' ? 1 : -1)
    commitMobileDayChange(nextDate)
    return nextDate
  }

  function animateMobileDayChange(direction: 'prev' | 'next', suppressClick = false) {
    if (!isMobile) {
      return
    }

    const baseDate = flushMobileSwipeMotion()
    if (direction === 'prev' && baseDate <= earliestVisibleDate) {
      animateMobileSwipeBack()
      return
    }

    const width = getMobileSwipeViewportWidth()
    if (width <= 0) {
      commitMobileDayChange(addDays(baseDate, direction === 'next' ? 1 : -1))
      return
    }

    const nextDate = addDays(baseDate, direction === 'next' ? 1 : -1)
    extendMobileRangeIfNeeded(nextDate)
    mobileSuppressCourseAreaClickRef.current = suppressClick

    mobileSwipeMotionRef.current = direction === 'next' ? 'settling-next' : 'settling-prev'
    const targetOffset = direction === 'next' ? -width : width
    setMobileSwipeTrackOffset(targetOffset, true, getMobileSwipeSettleDuration(targetOffset, width))
  }

  function animateMobileSwipeBack() {
    if (!isMobile || mobileSwipeMotionRef.current !== 'idle') {
      return
    }

    mobileSwipeMotionRef.current = 'snapback'
    setMobileSwipeTrackOffset(0, true, getMobileSwipeSettleDuration(0, getMobileSwipeViewportWidth()))
  }

  function handleMobileSwipeTransitionEnd() {
    const motion = mobileSwipeMotionRef.current
    if (motion === 'idle') {
      return
    }

    mobileSwipeMotionRef.current = 'idle'

    if (motion === 'snapback') {
      return
    }

    commitMobileDayChange(addDays(mobileSelectedDateRef.current, motion === 'settling-next' ? 1 : -1))
  }

  function resetMobileCourseAreaSwipe() {
    mobileCourseAreaSwipeRef.current.active = false
    mobileCourseAreaSwipeRef.current.axis = null
    mobileCourseAreaSwipeRef.current.triggered = false
  }

  function handleMobileCourseAreaTouchStart(event: ReactTouchEvent<HTMLDivElement>) {
    if (!isMobile) {
      return
    }

    const touch = event.touches[0]
    if (!touch) {
      return
    }

    flushMobileSwipeMotion()
    mobileSuppressCourseAreaClickRef.current = false
    mobileCourseAreaSwipeRef.current.active = true
    mobileCourseAreaSwipeRef.current.axis = null
    mobileCourseAreaSwipeRef.current.startX = touch.clientX
    mobileCourseAreaSwipeRef.current.startY = touch.clientY
    mobileCourseAreaSwipeRef.current.lastX = touch.clientX
    mobileCourseAreaSwipeRef.current.startTime = event.timeStamp
    mobileCourseAreaSwipeRef.current.lastTime = event.timeStamp
    mobileCourseAreaSwipeRef.current.triggered = false
    setMobileSwipeTrackOffset(0, false)
  }

  function handleMobileCourseAreaTouchMove(event: ReactTouchEvent<HTMLDivElement>) {
    if (!isMobile) {
      return
    }

    const touch = event.touches[0]
    if (!touch || !mobileCourseAreaSwipeRef.current.active || mobileCourseAreaSwipeRef.current.triggered) {
      return
    }

    const deltaX = touch.clientX - mobileCourseAreaSwipeRef.current.startX
    const deltaY = touch.clientY - mobileCourseAreaSwipeRef.current.startY
    const absoluteDeltaX = Math.abs(deltaX)
    const absoluteDeltaY = Math.abs(deltaY)
    mobileCourseAreaSwipeRef.current.lastX = touch.clientX
    mobileCourseAreaSwipeRef.current.lastTime = event.timeStamp

    if (!mobileCourseAreaSwipeRef.current.axis) {
      if (absoluteDeltaX < 8 && absoluteDeltaY < 8) {
        return
      }

      mobileCourseAreaSwipeRef.current.axis = absoluteDeltaX > absoluteDeltaY ? 'x' : 'y'
    }

    if (mobileCourseAreaSwipeRef.current.axis !== 'x') {
      return
    }

    event.preventDefault()

    const width = getMobileSwipeViewportWidth()
    if (deltaX > 0 && mobileSelectedDateRef.current <= earliestVisibleDate) {
      setMobileSwipeTrackOffset(getMobileSwipeOffsetWithResistance(deltaX * 0.2, width), false)
      return
    }

    const resistedOffset = getMobileSwipeOffsetWithResistance(deltaX, width)
    setMobileSwipeTrackOffset(resistedOffset, false)
  }

  function handleMobileCourseAreaTouchEnd() {
    if (!isMobile || mobileCourseAreaSwipeRef.current.axis !== 'x') {
      resetMobileCourseAreaSwipe()
      return
    }

    const deltaX = mobileCourseAreaSwipeRef.current.lastX - mobileCourseAreaSwipeRef.current.startX
    const absoluteDeltaX = Math.abs(deltaX)
    const elapsed = Math.max(
      mobileCourseAreaSwipeRef.current.lastTime - mobileCourseAreaSwipeRef.current.startTime,
      1,
    )
    const velocity = absoluteDeltaX / elapsed
    const swipeThreshold = getMobileSwipeThreshold(getMobileSwipeViewportWidth())
    const projectedTravel = absoluteDeltaX + velocity * MOBILE_SWIPE_PROJECTED_TRAVEL_MS

    if (
      absoluteDeltaX >= swipeThreshold ||
      projectedTravel >= swipeThreshold ||
      velocity >= MOBILE_SWIPE_VELOCITY_THRESHOLD
    ) {
      mobileCourseAreaSwipeRef.current.triggered = true
      animateMobileDayChange(deltaX < 0 ? 'next' : 'prev', true)
      resetMobileCourseAreaSwipe()
      return
    }

    animateMobileSwipeBack()
    resetMobileCourseAreaSwipe()
  }

  function handleMobileCourseAreaTouchCancel() {
    if (mobileCourseAreaSwipeRef.current.axis === 'x') {
      animateMobileSwipeBack()
    }

    resetMobileCourseAreaSwipe()
  }

  function handleMobileCourseAreaClickCapture(event: ReactMouseEvent<HTMLDivElement>) {
    if (!mobileSuppressCourseAreaClickRef.current) {
      return
    }

    event.preventDefault()
    event.stopPropagation()
    mobileSuppressCourseAreaClickRef.current = false
  }

  const renderMobileDaySlide = (dayDate: Date, slideKey = format(dayDate, 'yyyy-MM-dd')) => {
    const dayKey = format(dayDate, 'yyyy-MM-dd')
    const dayCourses = coursesByDate[dayKey] ?? []

    return (
      <div key={slideKey} className="motionlab-member-calendar-mobile-slide w-full min-w-0">
        <div className="w-full text-card-foreground">
          <div className="pb-4">
            <div
              className={cn(
                'relative flex min-h-7 items-center justify-center',
                isToday(dayDate) ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'max-w-full whitespace-nowrap font-nav text-sm font-semibold uppercase tracking-[0.14em] leading-none',
                  isToday(dayDate) ? 'text-primary' : 'text-foreground',
                )}
              >
                {format(dayDate, 'EEE, d. MMM', { locale: de })}
              </span>
              {isToday(dayDate) && (
                <div className="absolute top-full mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_rgba(var(--primary),0.8)]" />
              )}
            </div>
          </div>

          <ScrollFadeContainer className="motionlab-member-calendar-swipe-surface">
            {renderDayCourses(dayCourses)}
          </ScrollFadeContainer>
        </div>
      </div>
    )
  }

  const renderDayCourses = (dayCourses: CalendarCourse[]) => (
    <div className="w-full min-w-0 space-y-2 pt-1">
      {dayCourses.length > 0 ? (
        dayCourses.map((course) => (
          <div
            key={course.id}
            className="group w-full min-w-0 rounded-xl border border-border/80 bg-card p-3 text-card-foreground shadow transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md dark:border-border/50 dark:bg-muted/50 dark:shadow-none dark:hover:bg-muted"
          >
            <button
              type="button"
              onClick={() => openSessionDialog(course.session)}
              className="block w-full min-w-0 cursor-pointer text-left"
            >
              <h4 className="font-headline text-base font-semibold text-foreground transition-colors group-hover:text-primary whitespace-normal break-words [overflow-wrap:anywhere] line-clamp-2 md:text-sm">
                {course.name}
              </h4>
              <div className="mt-2 space-y-1.5 text-xs text-muted-foreground md:text-xs">
                <div className="flex items-center gap-1.5">
                  <Clock size={14} className="text-primary/70 md:h-3 md:w-3" />
                  <span className="font-nav font-medium">
                    {course.time} · {course.duration} Min
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Users size={14} className="text-primary/70 md:h-3 md:w-3" />
                  <span className="font-nav font-medium">{course.spots} Plätze frei</span>
                </div>
                {course.resourceName && (
                  <div className="flex items-center gap-1.5">
                    <MapPin size={14} className="text-primary/70 md:h-3 md:w-3" />
                    <span className="font-nav font-medium truncate">{course.resourceName}</span>
                  </div>
                )}
              </div>
            </button>

            <button
              type="button"
              onClick={() => openSessionDialog(course.session)}
              className={cn(
                'mt-2 inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-nav font-bold uppercase tracking-wider transition-colors md:text-[10px]',
                getActionClassName(course.state),
              )}
            >
              {getActionLabel(course.state)}
            </button>
          </div>
        ))
      ) : (
        <div className="flex min-h-[140px] h-full items-center justify-center rounded-xl border border-dashed border-border/30 bg-card/70 p-4 opacity-60 shadow-sm dark:bg-transparent dark:shadow-none">
          <span className="text-center font-nav text-sm font-semibold italic">Keine Kurse</span>
        </div>
      )}
    </div>
  )

  if (sessionsQuery.error) {
    return (
      <section className="motionlab-member-calendar-theme pb-8 pt-0 md:pb-10">
        <div className="mx-auto max-w-7xl px-4 text-center md:px-6 lg:px-8">
          <p className="text-red-400">Fehler beim Laden des Kalenders.</p>
        </div>
      </section>
    )
  }

  return (
    <div className="motionlab-member-calendar-page -mx-3 md:-mx-4 lg:-mx-5">
      <section className="motionlab-member-calendar-theme pb-8 pt-0 md:pb-10">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
          {outOfCredits && (
            <Alert className="mb-6 border-destructive/30 bg-destructive/10 text-foreground">
              <AlertDescription className="font-nav text-sm">
                Dein Guthaben ist aufgebraucht. Bitte lade Kursguthaben nach, um weitere Kurse buchen zu können.
              </AlertDescription>
            </Alert>
          )}

          <div className="mb-3 text-center">
            {!isMobile && (
              <div className="mt-2 grid grid-cols-3 items-center rounded-2xl border border-border/80 bg-card p-4 shadow md:mt-3 dark:border-border/50 dark:bg-muted/30 dark:shadow-none">
                <div className="flex justify-start">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handlePrevWeek}
                    disabled={isAtEarliestVisibleWeek}
                    className="h-10 w-10 rounded-full border-border bg-background/70 hover:border-primary/50 hover:bg-background dark:bg-transparent dark:hover:bg-primary/5"
                  >
                    <ChevronLeft size={20} />
                  </Button>
                </div>

                <div className="flex flex-col items-center gap-1">
                  <span className="font-nav text-center text-base font-semibold text-foreground md:text-lg">
                    {format(weekStart, 'd. MMM', { locale: de })} -{' '}
                    {format(weekEnd, 'd. MMM yyyy', { locale: de })}
                  </span>

                  {!isSameWeek(currentDate, new Date(), { weekStartsOn: 1 }) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleToday}
                      className="h-6 rounded-full px-3 py-0 font-nav text-[10px] font-bold uppercase tracking-wider text-primary hover:bg-primary/10"
                    >
                      Heute
                    </Button>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleNextWeek}
                    className="h-10 w-10 rounded-full border-border bg-background/70 hover:border-primary/50 hover:bg-background dark:bg-transparent dark:hover:bg-primary/5"
                  >
                    <ChevronRight size={20} />
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            {sessionsQuery.isLoading && (
              isMobile ? (
                <div className="pointer-events-none absolute inset-x-0 top-28 z-20">
                  <div className="flex justify-center px-1">
                    <div className="flex items-center gap-3 rounded-2xl border border-border/80 bg-card/95 px-4 py-3 shadow backdrop-blur-sm dark:border-border/50 dark:bg-muted/80 dark:shadow-none">
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
                      <span className="font-nav text-sm font-medium text-muted-foreground">
                        Lade Kurse...
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl border border-border/50 bg-card/50 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-3">
                    <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
                    <span className="font-nav text-sm font-medium text-muted-foreground">
                      Lade Kurse...
                    </span>
                  </div>
                </div>
              )
            )}

            {isMobile ? (
              <div className="space-y-3 pt-28">
                <div className="fixed inset-x-0 top-[4.75rem] z-30 px-4 md:hidden">
                  <div className="mx-auto grid max-w-7xl gap-3">
                    <div className="flex justify-center">
                      <Button size="sm" onClick={handleToday} className="font-nav shadow-sm">
                        <CalendarIcon size={16} className="mr-1.5" />
                        Zum heutigen Datum
                      </Button>
                    </div>

                    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 rounded-2xl border border-border/80 bg-card/95 px-3 py-2 shadow backdrop-blur-sm dark:border-border/50 dark:bg-muted/80 dark:shadow-none">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => animateMobileDayChange('prev')}
                        disabled={isAtEarliestVisibleMobileDate}
                        className="justify-self-start rounded-full border-border bg-background/70 text-foreground hover:border-primary/50 hover:bg-background dark:bg-transparent dark:hover:bg-primary/5"
                      >
                        <ChevronLeft size={18} />
                      </Button>

                      <p className="px-2 text-center font-nav text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                        Wische zum Wechseln der Tage
                      </p>

                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => animateMobileDayChange('next')}
                        className="justify-self-end rounded-full border-border bg-background/70 text-foreground hover:border-primary/50 hover:bg-background dark:bg-transparent dark:hover:bg-primary/5"
                      >
                        <ChevronRight size={18} />
                      </Button>
                    </div>
                  </div>
                </div>

                <div
                  ref={mobileSwipeViewportRef}
                  className="motionlab-member-calendar-mobile-viewport"
                  onTouchStartCapture={handleMobileCourseAreaTouchStart}
                  onTouchMoveCapture={handleMobileCourseAreaTouchMove}
                  onTouchEndCapture={handleMobileCourseAreaTouchEnd}
                  onTouchCancelCapture={handleMobileCourseAreaTouchCancel}
                  onClickCapture={handleMobileCourseAreaClickCapture}
                >
                  <div
                    ref={mobileSwipeTrackRef}
                    className="motionlab-member-calendar-mobile-track"
                    onTransitionEnd={handleMobileSwipeTransitionEnd}
                  >
                    {mobileVisibleDays.map((dayDate, index) =>
                      renderMobileDaySlide(dayDate, `${format(dayDate, 'yyyy-MM-dd')}-${index}`),
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div
                ref={desktopWeekScrollContainerRef}
                className="overflow-x-auto pb-4 snap-x snap-mandatory touch-scrollbar-hidden min-[1030px]:overflow-visible"
              >
                <div className="min-w-full">
                  <div className="flex min-w-full gap-3 min-[1030px]:grid min-[1030px]:grid-cols-7">
                    {DAYS.map((day, dayIndex) => {
                      const dayDate = addDays(weekStart, dayIndex)

                      return (
                        <div
                          key={`${day}-header`}
                          className={cn(
                            desktopDayColumnClassName,
                            dayIndex === initialHorizontalDayIndex && 'motionlab-member-calendar-current-day',
                          )}
                        >
                          <div
                            className={cn(
                              'mb-3 flex flex-col items-center gap-0.5 text-center',
                              isToday(dayDate) ? 'text-primary' : 'text-muted-foreground',
                            )}
                          >
                            <span className="font-nav text-xs font-bold uppercase tracking-tighter opacity-70">
                              {day}
                            </span>
                            <span
                              className={cn(
                                'font-headline text-xl font-black',
                                isToday(dayDate) ? 'text-primary' : 'text-foreground',
                              )}
                            >
                              {format(dayDate, 'd')}
                            </span>
                            <div className="mt-0.5 flex h-2 items-start">
                              {isToday(dayDate) && (
                                <div className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_rgba(var(--primary),0.8)]" />
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <ScrollFadeContainer className="motionlab-member-calendar-desktop-scroll mt-1">
                    <div className="flex min-w-full items-start gap-3 min-[1030px]:grid min-[1030px]:grid-cols-7">
                      {DAYS.map((day, dayIndex) => {
                        const dayDate = addDays(weekStart, dayIndex)
                        const dayCourses = coursesByDate[format(dayDate, 'yyyy-MM-dd')] ?? []

                        return (
                          <div
                            key={day}
                            className={cn(
                              desktopDayColumnClassName,
                              dayIndex === initialHorizontalDayIndex && 'motionlab-member-calendar-current-day',
                            )}
                          >
                            {renderDayCourses(dayCourses)}
                          </div>
                        )
                      })}
                    </div>
                  </ScrollFadeContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <SessionDetailDialog
        open={bookingSession !== null || cancellingBooking !== null}
        onOpenChange={(open) => {
          if (!open) {
            setBookingSession(null)
            setCancellingBooking(null)
          }
        }}
        session={
          cancellingBooking
            ? bookingToSessionDetail(cancellingBooking)
            : bookingSession
              ? publicSessionToDetail(bookingSession)
              : null
        }
        title={cancellingBooking ? 'Kursdetails' : 'Kurs buchen'}
        description={
          cancellingBooking
            ? 'Informationen zu deinem gebuchten Kurs.'
            : 'Möchtest du dich für folgenden Kurs anmelden?'
        }
        footer={
          bookingSession ? (
            <>
              {(isBookingSessionPast || isBookingSessionFull || isBookingDeadlinePassed || outOfCredits) ? (
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs text-muted-foreground sm:flex-1">
                    {isBookingSessionPast
                      ? 'Dieser Kurs liegt in der Vergangenheit.'
                      : isBookingSessionFull
                        ? 'Dieser Kurs ist leider ausgebucht.'
                        : isBookingDeadlinePassed
                          ? 'Die Buchungsfrist ist bereits abgelaufen.'
                          : 'Dein Guthaben ist aufgebraucht.'}
                  </p>
                  <div className="flex w-full justify-end gap-2 sm:w-auto sm:shrink-0">
                    <Button variant="outline" onClick={() => setBookingSession(null)}>
                      Schließen
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="mb-2 w-full text-xs text-muted-foreground">
                    Es wird 1 Kurseinheit von deinem Guthaben abgezogen, bzw. eine Buchung im Rahmen deines Monatsabos erstellt.
                  </p>
                  <div className="flex w-full justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setBookingSession(null)}
                      disabled={bookMutation.isPending}
                    >
                      Abbrechen
                    </Button>
                    <Button
                      onClick={() => bookingSession && bookMutation.mutate(bookingSession.session_id)}
                      disabled={bookMutation.isPending}
                    >
                      {bookMutation.isPending ? (
                        <>
                          <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                          Wird gebucht...
                        </>
                      ) : (
                        'Jetzt buchen'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </>
          ) : undefined
        }
        onCancel={cancellingBooking ? () => cancelMutation.mutate(cancellingBooking.id) : undefined}
        cancelPending={cancelMutation.isPending}
      />
    </div>
  )
}
